import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowRight, Calendar, Clock, Phone } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const blogPosts = [
  {
    id: 1,
    title: "House Cleaning Services Near Me: Why Local Matters",
    slug: "house-cleaning-services-near-me",
    excerpt: "Looking for house cleaning services in Whakatane? Discover why choosing a local cleaning crew makes all the difference for your home.",
    category: "Residential Cleaning",
    readTime: "5 min read",
    date: "Dec 15, 2024",
    image: "https://images.unsplash.com/photo-1581578731548-c64695cc6952"
  },
  {
    id: 2,
    title: "Deep Cleaning Services: What's Included and Why You Need It",
    slug: "deep-cleaning-services-guide",
    excerpt: "Deep cleaning goes beyond regular maintenance. Learn what's included in professional deep cleaning and when you should book one.",
    category: "Deep Cleaning",
    readTime: "7 min read",
    date: "Dec 10, 2024",
    image: "https://images.unsplash.com/photo-1563453392212-326f5e854473"
  },
  {
    id: 3,
    title: "End of Tenancy Cleaning: Get Your Bond Back Guaranteed",
    slug: "end-of-tenancy-cleaning-checklist",
    excerpt: "Moving out? Our comprehensive end of tenancy cleaning checklist ensures you get your full bond back. No stress, no worries.",
    category: "Moving Services",
    readTime: "6 min read",
    date: "Dec 5, 2024",
    image: "https://images.unsplash.com/photo-1527515637462-cff94eecc1ac"
  },
  {
    id: 4,
    title: "Commercial Cleaning Services: Keep Your Business Spotless",
    slug: "commercial-cleaning-services",
    excerpt: "First impressions matter. Learn how professional commercial cleaning can boost your business image and employee productivity.",
    category: "Commercial",
    readTime: "5 min read",
    date: "Nov 28, 2024",
    image: "https://images.unsplash.com/photo-1497366216548-37526070297c"
  },
  {
    id: 5,
    title: "Window Cleaning: Benefits of Crystal Clear Views",
    slug: "professional-window-cleaning",
    excerpt: "Dirty windows can reduce natural light by up to 40%. Discover the benefits of professional window cleaning services.",
    category: "Specialized Services",
    readTime: "4 min read",
    date: "Nov 20, 2024",
    image: "https://images.unsplash.com/photo-1527515545081-5db817172677"
  },
  {
    id: 6,
    title: "Spring Cleaning Checklist 2024: Room by Room Guide",
    slug: "spring-cleaning-checklist",
    excerpt: "Ready for spring? Our ultimate room-by-room cleaning checklist will help you tackle every corner of your home efficiently.",
    category: "Tips & Guides",
    readTime: "8 min read",
    date: "Nov 15, 2024",
    image: "https://images.unsplash.com/photo-1582735689369-4fe89db7114c"
  },
  {
    id: 7,
    title: "How to Prepare for Professional Cleaners",
    slug: "prepare-for-professional-cleaners",
    excerpt: "Get the most out of your cleaning service. Simple steps to prepare your home before the professionals arrive.",
    category: "Tips & Guides",
    readTime: "4 min read",
    date: "Nov 8, 2024",
    image: "https://images.unsplash.com/photo-1585421514738-01798e348b17"
  },
  {
    id: 8,
    title: "Oven Cleaning: Why DIY Isn't Worth the Hassle",
    slug: "professional-oven-cleaning",
    excerpt: "Struggling with a greasy oven? Learn why professional oven cleaning saves time, effort, and delivers better results.",
    category: "Specialized Services",
    readTime: "5 min read",
    date: "Nov 1, 2024",
    image: "https://images.unsplash.com/photo-1556912173-46c336c7fd55"
  },
  {
    id: 9,
    title: "Builders Cleaning: Post-Construction Clean-Up Made Easy",
    slug: "post-construction-cleaning",
    excerpt: "Just finished renovations? Professional builders cleaning removes dust, debris, and makes your space move-in ready.",
    category: "Specialized Services",
    readTime: "6 min read",
    date: "Oct 25, 2024",
    image: "https://images.unsplash.com/photo-1581578731548-c64695cc6952"
  }
];

const BlogPage = () => {
  const navigate = useNavigate();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            <div className="flex items-center">
              <a href="/" className="flex flex-col">
                <span className="text-3xl font-black text-gray-900 tracking-tight leading-none">
                  THE CLEANING CREW
                </span>
                <span className="text-sm font-semibold text-red-600 tracking-wider uppercase mt-1">
                  Whakatane
                </span>
              </a>
            </div>
            <nav className="hidden md:flex items-center space-x-8">
              <a href="/" className="text-gray-700 hover:text-red-600 transition-colors font-semibold text-lg">Home</a>
              <a href="/blog" className="text-red-600 font-bold text-lg">Blog</a>
              <a href="tel:0225896143" className="flex items-center space-x-2 text-gray-700 hover:text-red-600 transition-colors">
                <Phone className="w-5 h-5" />
                <span className="font-semibold text-lg">0225896143</span>
              </a>
              <a href="/">
                <Button className="bg-red-600 hover:bg-red-700 text-white font-bold px-6 py-6 text-lg shadow-lg">
                  Get a Quote
                </Button>
              </a>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-36 pb-16 bg-gradient-to-br from-red-600 to-red-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Cleaning Tips & Guides
            </h1>
            <p className="text-xl text-white/90 mb-8">
              Expert advice, cleaning tips, and everything you need to know about professional cleaning services in Whakatane
            </p>
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {blogPosts.map((post) => (
              <Card 
                key={post.id}
                className="group overflow-hidden border-0 shadow-lg hover:shadow-premium-lg transition-all duration-300 transform hover:scale-105 cursor-pointer"
                onClick={() => navigate(`/blog/${post.slug}`)}
              >
                <div className="relative h-56 overflow-hidden">
                  <img 
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                      {post.category}
                    </span>
                  </div>
                </div>
                <CardHeader>
                  <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                    <div className="flex items-center space-x-1">
                      <Calendar className="w-4 h-4" />
                      <span>{post.date}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Clock className="w-4 h-4" />
                      <span>{post.readTime}</span>
                    </div>
                  </div>
                  <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                    {post.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base text-gray-600 leading-relaxed mb-4">
                    {post.excerpt}
                  </CardDescription>
                  <div className="flex items-center text-blue-600 font-semibold group-hover:gap-2 transition-all">
                    Read More <ArrowRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-gray-900 to-gray-800">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Experience Professional Cleaning?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Don't just read about it. Get your free quote today!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:0225896143">
                <Button size="lg" className="w-full sm:w-auto bg-white text-gray-900 hover:bg-gray-100 text-lg px-10 py-7 font-bold">
                  <Phone className="w-6 h-6 mr-2" />
                  Call 0225896143
                </Button>
              </a>
              <a href="/">
                <Button 
                  size="lg" 
                  className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white text-lg px-10 py-7 font-bold"
                >
                  Get Free Quote
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-8">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400">&copy; 2024 The Cleaning Crew. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default BlogPage;
